import { ref, reactive } from 'vue'
import request from '@/util/request'

// ==================== 枚举缓存和查询方法 ====================
const enumCache = new Map()
const cstCache = new Map()
const CACHE_MAX_COUNT = 10000;

export const getEnumList = async (cacheKey, params) => {
    if (enumCache.size > CACHE_MAX_COUNT) {
        enumCache.clear();
    }
    if (enumCache.has(cacheKey)) {
        return enumCache.get(cacheKey)
    }
    try {
        const res = await request({
            url: '/enum/getEnumsByMap',
            method: 'get',
            params: params
        })
        const enumList = res.data || []
        const validList = enumList.map(item => ({
            enumCodeName: item.enumCodeName,
            code: item.code
        })).filter(item => item.enumCodeName && item.code !== undefined)

        enumCache.set(cacheKey, validList)
        return validList
    } catch (error) {
        console.error(`获取枚举[${cacheKey}]失败：`, error)
        enumCache.delete(cacheKey)
        return []
    }
}

export const enumSelect = async (enumKey, enumValue,map) => {
    if (!enumKey || !enumValue) { return [] }
    const cacheKey = `${enumKey}_${enumValue}`
    const params = { [enumKey]: enumValue,...map }
    const list = await getEnumList(cacheKey, params)
    return list;
}

/**
 * 客户下拉分页管理器（重构版）
 * @param {Object} options - 配置选项
 * @param {number} [options.pageSize=10] - 每页条数（默认10条）
 * @returns {Object} 分页管理器对象
 */
export const useCstSelect = (options = {}) => {
    const { pageSize = 10 } = options; // 默认每页10条，可外部配置

    const state = reactive({
        cstList: [],       // 客户列表数据
        currentPage: 1,    // 当前页码
        currentQuery: '',  // 当前查询关键词
        isLoading: false,  // 是否正在加载
        hasMore: true      // 是否还有更多数据
    });

    // 生成缓存key（关键词+页码）
    const generateCacheKey = (query, page,data) => {
        const baseParams = query ? { cstData: query } : {};
        return JSON.stringify({ ...baseParams, pageNum: page, pageSize,data });
    };

    // 构建查询参数
    const getQueryParams = (query, page,data) => {
        const params = {
            isDeleted: 0,//有效的
            pageNum: page,
            pageSize: pageSize
        };

        //输入框参数
        if (query) {
            let cstKey = "likecstName";
            let val = query.toString().trim();
            const reg = /^1\d{0,10}$/;
            if (reg.test(val)) {
                cstKey = "likeCstPhone";
            } else if (val.toUpperCase().startsWith("CST")) {
                cstKey = "likeCstId";
            }
            params[cstKey] = query;

        }
        //其余参数
        if(data){
            // 把 data 的所有 key:value 全部合并到 params
            Object.assign(params, data);
        }

        return params;
    };

    // 加载指定页数据（核心查询方法）
    const loadPage = async (query, page,data) => {
        const cacheKey = generateCacheKey(query, page,data);

        // 缓存存在直接返回
        if (cstCache.has(cacheKey)) {
            return cstCache.get(cacheKey);
        }

        // 缓存不存在查后台
        const params = getQueryParams(query, page,data);
        const res = await request({
            url: '/cstmgt/getCstList',
            method: 'get',
            params: params
        });

        if (res && res.code == 200 && res.data) {
            const cstList = res.data.records || [];
            const validList = cstList.map(item => ({
                id: item.id,
                cstId: item.cstId,
                cstPhone: item.cstPhone,
                cstName: item.cstName,
                total: res.data.total
            })).filter(item => item && item.id && item.cstId !== undefined);

            cstCache.set(cacheKey, validList);
            return validList;
        }

        return [];
    };

    /**
     * 加载数据（核心方法：替代原loadFirstPage+loadNextPage）
     * @param {string} query - 查询关键词
     * @param {boolean} isPagination - 是否分页（false=只查第1页，true=加载下一页）
     * data 其余参数{key:value}
     */
    const loadNextPage = async (query = '', isPagination = false,data) => {
        if (state.isLoading) return; // 防止重复加载
        state.isLoading = true;

        try {
            // 1. 不分页：重置页码为1，清空列表
            if (!isPagination) {
                state.currentQuery = query;
                state.currentPage = 1;
                state.cstList = []; // 清空原有数据
                state.hasMore = true;
            }

            // 2. 分页：页码+1；不分页：页码固定为1
            const targetPage = isPagination ? state.currentPage + 1 : 1;

            // 3. 加载目标页数据
            const list = await loadPage(query, targetPage,data);

            // 4. 无数据不再加载（如果是最后一页无论翻页参数是多少都只会返最后一页数据所有得有7兜底）
            if (list.length === 0) {
                state.hasMore = false;
                return;
            }

            // 5. 缓存存在=已加载过，不拼接；否则拼接数据
            const cacheKey = generateCacheKey(query, targetPage,data);
            if (!cstCache.has(cacheKey)) return;

            // 6. 拼接数据（不分页=直接替换，分页=追加）
            if (!isPagination) {
                state.cstList = list;
            } else {
                state.cstList = [...state.cstList, ...list];
               //7.拼接后的总条数 >= 总条数 不再加载
                if(state.cstList.length  >= list[0].total){
                    state.hasMore = false;
                    return;
                }
                state.currentPage = targetPage; // 更新当前页码
            }

            // 8. 判断是否还有更多（数据条数 =页大小= 这一页满了还有下一页）
            state.hasMore = list.length === pageSize;

        } catch (error) {
            console.error('加载客户列表失败：', error);
            state.hasMore = false;
        } finally {
            state.isLoading = false;
        }
    };

    /**
     * 打开下拉框时给下拉框添加监听下拉时间 下拉到底部预定px时触发查询方法（触底加载下一页）
     * @param {boolean} visible - 下拉框是否可见
     * @param {Ref} selectRef - 下拉框Ref
     * @param {Function} loadCallback - 加载下一页的回调
     * loadNextPage 查询方法 反复调用会一直加载下一页直到最后一页
     */
    const handleSelectVisibleChange = (visible, selectRef, loadCallback) => {
        // 先移除旧监听（无论是否可见，先清理）
        const popper = selectRef?.value?.popperRef?.contentRef;
        if (popper) {
            const scrollWrap = popper.querySelector('.el-select-dropdown__wrap');
            if (scrollWrap && scrollWrap._scrollListener) {
                scrollWrap.removeEventListener('scroll', scrollWrap._scrollListener);
                delete scrollWrap._scrollListener;
            }
        }
        if (visible) {
            setTimeout(() => {
                if (!selectRef.value) return

                // 👇 这是 Element Plus 2.12 永远有效的写法
                const popper = selectRef.value.popperRef
                if (!popper) return

                const scrollWrap = popper.querySelector('.el-select-dropdown__wrap')
                if (!scrollWrap) return

                // 滚动监听（20px 触发）
                const scrollListener = () => {
                    if (state.isLoading || !state.hasMore) return

                    const { scrollTop, scrollHeight, clientHeight } = scrollWrap
                    const distanceToBottom = scrollHeight - (scrollTop + clientHeight)

                    // 距离底部 20px 内触发
                    if (distanceToBottom <= 20) {
                        loadCallback?.()
                    }
                }

                scrollWrap._scrollListener = scrollListener
                scrollWrap.addEventListener('scroll', scrollListener)
            }, 100)
        }
    };

    return {
        // 响应式数据（只读）
        get cstList() { return state.cstList; },
        get isLoading() { return state.isLoading; },
        get hasMore() { return state.hasMore; },
        get currentPage() { return state.currentPage; },
        get currentQuery() { return state.currentQuery; },
        // 核心方法
        loadNextPage,
        handleSelectVisibleChange
    };
};

// ==================== 列表通用方法 ====================
export function listCommon(searchForm) {
    const oneShow = ref(null)
    const listShow = ref(null)

    const handleReset = () => {
        Object.keys(searchForm).forEach((key) => {
            if (Array.isArray(searchForm[key])) {
                searchForm[key] = []
            } else {
                searchForm[key] = ''
            }
        })
    }

    const handleSelectionChange = (val) => {
        listShow.value = val?.length ? val : null
        if (!val?.length) {
            listShow.value = null;
            oneShow.value = null;
            return;
        }
        // selection-type="radio" 时，val 是一个长度为 1 的数组
        oneShow.value = val.length === 1 ? val[0] : null;
    }

    const handleCurrentRowChange = (val) => {
        listShow.value = val
        oneShow.value = val
    }

    return {
        oneShow,
        listShow,
        handleReset,
        handleSelectionChange,
        handleCurrentRowChange
    }
}

// ==================== 工具方法 ====================
export const getTimeUUID = async (randomLength = 6, prefix = '') => {
    try {
        const now = new Date();
        const timestamp = now.getTime().toString(36);
        const microsecond = now.getMilliseconds().toString().padStart(3, '0');
        const timeCode = `${timestamp}${microsecond}`;

        const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
        const charsLen = chars.length;
        let randomStr = '';

        const randomValues = typeof crypto !== 'undefined'
            ? crypto.getRandomValues(new Uint8Array(randomLength))
            : Array.from({ length: randomLength }, () => Math.floor(Math.random() * 256));

        for (let i = 0; i < randomLength; i++) {
            randomStr += chars[randomValues[i] % charsLen];
        }

        const uuid = prefix ? `${prefix}${timeCode}${randomStr}` : `${timeCode}${randomStr}`;
        return uuid;
    } catch (e) {
        console.warn('生成时间UUID失败，降级生成', e);
        const fallbackId = `${prefix}_${Date.now()}_${Math.random().toString(36).slice(-randomLength)}`;
        return fallbackId.replace(/^_/, '');
    }
}

export const getDate = async (format = 'YYYY-MM-DD HH:mm:ss') => {
    try {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        return format
            .replace('YYYY', year)
            .replace('MM', month)
            .replace('DD', day)
            .replace('HH', hours)
            .replace('mm', minutes)
            .replace('ss', seconds);
    } catch (e) {
        return
    }
}

// ==================== 插件注册 ====================
export default {
    install(app) {
        app.config.globalProperties.$commonList = {
            getEnumList,
            enumSelect,
            useCstSelect,
            listCommon,
            getTimeUUID,
            getDate
        }
    }
}